import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import { createContext, createElement, useContext, type ReactNode } from "react";

import type { ApiClient } from "./client.js";
import { ApiError } from "./api-error.js";

const ApiClientContext = createContext<ApiClient | null>(null);

export function ApiClientProvider(props: { client: ApiClient; children: ReactNode }): ReactNode {
  return createElement(ApiClientContext.Provider, { value: props.client }, props.children);
}

export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);
  if (!client) throw new Error("useApiClient must be used inside an ApiClientProvider.");
  return client;
}

/**
 * A typed GET, wired to TanStack Query.
 *
 * Retries are off for 4xx: a 401, 403 or 404 will not become a different answer by being asked
 * again, and retrying a 401 fights the client's own refresh-once logic.
 */
export function useApiQuery<T>(
  key: readonly unknown[],
  path: string,
  options?: Omit<UseQueryOptions<T, ApiError>, "queryKey" | "queryFn">,
): UseQueryResult<T, ApiError> {
  const client = useApiClient();
  return useQuery<T, ApiError>({
    queryKey: key,
    queryFn: () => client.get<T>(path),
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status >= 400 && error.status < 500
        ? false
        : failureCount < 2,
    ...options,
  });
}

/**
 * A typed mutation.
 *
 * `invalidates` is the common case made declarative — after a successful write, drop these query
 * keys. Without it every call site hand-rolls an `onSuccess`, and one of them forgets.
 *
 * `discardsEverything` is the rare case, and it exists for exactly one reason: switching the active
 * household. Every cached entry was fetched in the previous household's scope, so naming keys would
 * mean maintaining a list that silently goes stale the moment a new context adds a query — and the
 * failure that produces is another household's numbers rendered under this household's name.
 *
 * IT DISCARDS AS WELL AS INVALIDATES, and the difference is the point. `invalidateQueries` alone
 * marks entries stale and refetches them, but LEAVES THE OLD DATA IN THE CACHE — safe only while
 * every query key contains the household id. Every key does today. That is a convention, and 006
 * through 013 will add a great many more; the first one that forgets makes a component render
 * another household's data under this household's name, and worse, serve it INSTANTLY from cache
 * when you switch back.
 *
 * So the two kinds of entry are treated differently, and the split is not arbitrary:
 *
 *   - INACTIVE (nothing mounted is reading them) are REMOVED. These are the dangerous ones: they
 *     survive unobserved and are handed straight to the next component that asks. After a switch
 *     there is nothing of the previous household left for them to hand over.
 *   - ACTIVE are seeded and invalidated, so they refetch under the new household. They are NOT
 *     removed, and that is a correctness requirement rather than an optimisation: removing an
 *     active query detaches its observers, which unmounts whatever was reading it — including, in
 *     the household switcher's case, the component running this very callback. The switch then
 *     never completes. Learned the hard way; there is an E2E test that goes red on it.
 *
 * Reach for `invalidates` unless the scope of every query has genuinely changed underneath you.
 *
 * It also keeps `useQueryClient` out of the domain packages' `/ui` components, which have
 * `@tanstack/react-query` as a peer rather than a dependency.
 */
export function useApiMutation<TResult, TInput = void>(
  request: (client: ApiClient, input: TInput) => Promise<TResult>,
  options?: Omit<UseMutationOptions<TResult, ApiError, TInput>, "mutationFn"> & {
    invalidates?: readonly (readonly unknown[])[];
    discardsEverything?: boolean;
    /** Writes a result straight into the cache before invalidation, avoiding a visible gap. */
    seeds?: readonly unknown[];
  },
): UseMutationResult<TResult, ApiError, TInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { invalidates, discardsEverything, seeds, ...mutationOptions } = options ?? {};

  return useMutation<TResult, ApiError, TInput>({
    mutationFn: (input: TInput) => request(client, input),
    ...mutationOptions,
    // Args are forwarded rather than destructured: TanStack has changed this callback's arity
    // between minor versions, and naming the parameters here would break on the next bump.
    onSuccess: async (...args: Parameters<NonNullable<typeof mutationOptions.onSuccess>>) => {
      if (discardsEverything === true) {
        // Drop only the INACTIVE entries. Removing active ones detaches their observers mid-render,
        // which unmounts whatever was reading them — including the switcher that is running this
        // very callback — and the switch then does not complete. Active queries are handled by the
        // seed plus the invalidation below, which refetches them under the new household.
        queryClient.removeQueries({ type: "inactive" });
      }

      if (seeds) queryClient.setQueryData(seeds, args[0]);

      if (discardsEverything === true) {
        // Nothing is cached now, so this only kicks the mounted queries into refetching.
        await queryClient.invalidateQueries();
      } else {
        await Promise.all(
          (invalidates ?? []).map((key) => queryClient.invalidateQueries({ queryKey: key })),
        );
      }

      await mutationOptions.onSuccess?.(...args);
    },
  });
}

/**
 * A typed GET that pages, wired to TanStack Query's infinite machinery.
 *
 * ==========================================================================================
 * IT KNOWS NOTHING ABOUT CURSORS
 * ==========================================================================================
 *
 * The caller supplies `nextPageParam`, which turns a page's data into the token for the next request, and
 * `path`, which turns that token into a URL. This hook never inspects a response body. That is deliberate:
 * the activity feed is the first thing in this application to page, and a hook shaped around the activity
 * feed's response would be a hook the second caller copies rather than uses.
 *
 * Returning `undefined` from `nextPageParam` is how a caller says "there is no next page" — TanStack stops
 * asking and `hasNextPage` becomes false. A caller must NOT signal the end by returning a page that
 * happens to be short: a filtered page can be short with more behind it.
 *
 * ==========================================================================================
 * THE PAGE TOKEN IS A `string`, NOT A TYPE PARAMETER
 * ==========================================================================================
 *
 * A generic `TPageParam` is the obvious shape and does not typecheck against TanStack v5:
 * `QueryFunctionContext` declares `pageParam?: unknown` in one arm of a union, so a generic token widens
 * to `unknown` inside `queryFn` and no overload matches. Working around it needs a cast, which would put
 * an unchecked assertion in the one place every future pager passes through.
 *
 * A string costs nothing here. A page token travels in a query string, so it is a string by the time it
 * matters; a caller paging by number formats it, and is no worse off than it would be building the URL.
 *
 * `retry` matches `useApiQuery`'s: off for 4xx, because a 401, 403, 404 or 422 will not become a different
 * answer by being asked again, and retrying a 401 fights the client's own refresh-once logic.
 */
export function useApiInfiniteQuery<T>(
  key: readonly unknown[],
  path: (pageParam: string | undefined) => string,
  options: {
    /** The next page's token, or `undefined` when the last page has been reached. */
    nextPageParam: (lastPage: T) => string | undefined;
    staleTime?: number;
  },
): UseInfiniteQueryResult<InfiniteData<T, string | undefined>, ApiError> {
  const client = useApiClient();

  return useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => client.get<T>(path(pageParam)),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: T) => options.nextPageParam(lastPage),
    ...(options.staleTime === undefined ? {} : { staleTime: options.staleTime }),
    retry: (failureCount: number, error: ApiError) =>
      error instanceof ApiError && error.status >= 400 && error.status < 500
        ? false
        : failureCount < 2,
  });
}
